import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE } from "utils";
import CardContent from "@mui/material/CardContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

function RekapanLaporanDinas() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [posisiFilter, setPosisiFilter] = useState({
    diKota: true,
    diLuarKota: false,
  });
  const [tanggalAwal, setTanggalAwal] = useState("");
  const [tanggalAkhir, setTanggalAkhir] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Set default tanggal: tanggal 1 bulan ini sampai hari ini dengan WITA (UTC+8)
  useEffect(() => {
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);

    const firstDayOfMonth = new Date(
      witaDate.getFullYear(),
      witaDate.getMonth(),
      1
    );

    setTanggalAwal(firstDayOfMonth.toISOString().split("T")[0]);
    setTanggalAkhir(witaDate.toISOString().split("T")[0]);
  }, []);

  // Fetch data dari API
  const fetchData = async () => {
    if (!tanggalAwal || !tanggalAkhir) {
      swal("Error", "Data tanggal tidak valid", "error");
      return;
    }

    if (new Date(tanggalAwal) > new Date(tanggalAkhir)) {
      swal(
        "Error",
        "Tanggal awal tidak boleh lebih besar dari tanggal akhir",
        "error"
      );
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}laporan-harian/rekapan/dinas`,
        {
          params: {
            tanggal_awal: tanggalAwal,
            tanggal_akhir: tanggalAkhir,
          },
        }
      );

      setData(response.data);
      // Filter data berdasarkan posisi yang dipilih
      filterDataByPosisi(response.data.data_dinas || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      swal("Error", "Gagal mengambil data. Silakan coba lagi.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Filter data berdasarkan posisi
  const filterDataByPosisi = (dataToFilter) => {
    if (!dataToFilter) return [];

    let filtered = dataToFilter;

    // Filter berdasarkan posisi yang dipilih
    if (posisiFilter.diKota && !posisiFilter.diLuarKota) {
      // Hanya posisi 0 (di kota)
      filtered = dataToFilter.filter((item) => item.posisi === 0);
    } else if (!posisiFilter.diKota && posisiFilter.diLuarKota) {
      // Hanya posisi 1 (di luar kota)
      filtered = dataToFilter.filter((item) => item.posisi === 1);
    } else if (posisiFilter.diKota && posisiFilter.diLuarKota) {
      // Kedua posisi
      filtered = dataToFilter;
    } else {
      // Tidak ada yang dipilih, tampilkan kosong
      filtered = [];
    }

    setFilteredData(filtered);
  };

  // Hitung statistik berdasarkan data yang terfilter
  const calculateFilteredStats = (filteredData) => {
    const totalDinas = filteredData.length;
    const totalPegawai = filteredData.reduce(
      (sum, row) => sum + row.total_pegawai,
      0
    );
    const totalLaporan = filteredData.reduce(
      (sum, row) => sum + row.total_laporan,
      0
    );
    const totalKegiatan = filteredData.reduce(
      (sum, row) => sum + row.total_kegiatan,
      0
    );
    const totalOutput = filteredData.reduce(
      (sum, row) => sum + row.total_output,
      0
    );

    // Hitung hari kerja dari data yang terfilter
    const hariKerja = data?.periode?.hari_kerja || 0;
    const totalLaporanSeharusnya = totalPegawai * hariKerja;
    const persentaseKepatuhanKeseluruhan =
      totalLaporanSeharusnya > 0
        ? ((totalLaporan / totalLaporanSeharusnya) * 100).toFixed(2)
        : 0;
    const rataRataKegiatanPerHari =
      hariKerja > 0 ? (totalKegiatan / hariKerja).toFixed(2) : 0;

    return {
      total_dinas: totalDinas,
      total_pegawai: totalPegawai,
      total_laporan: totalLaporan,
      total_kegiatan: totalKegiatan,
      total_output: totalOutput,
      persentase_kepatuhan_keseluruhan: parseFloat(
        persentaseKepatuhanKeseluruhan
      ),
      rata_rata_kegiatan_per_hari: parseFloat(rataRataKegiatanPerHari),
      total_laporan_seharusnya: totalLaporanSeharusnya,
    };
  };

  // Statistik terfilter, dihitung tanpa memodifikasi state `data` untuk menghindari loop update
  const filteredStats = useMemo(() => {
    if (!data) return null;
    return calculateFilteredStats(filteredData);
  }, [filteredData, data]);

  // Handle checkbox change
  const handlePosisiChange = (event) => {
    const { name, checked } = event.target;
    setPosisiFilter((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Auto-fetch data ketika tanggal berubah
  useEffect(() => {
    if (tanggalAwal && tanggalAkhir && !loading) {
      fetchData();
    }
  }, [tanggalAwal, tanggalAkhir]);

  // Filter data ketika posisi berubah
  useEffect(() => {
    if (data && data.data_dinas) {
      filterDataByPosisi(data.data_dinas);
      setCurrentPage(0); // Reset ke halaman pertama ketika filter berubah
    }
  }, [posisiFilter, data]);

  // Reset pagination ketika data berubah
  useEffect(() => {
    setCurrentPage(0);
  }, [filteredData]);

  // Navigate ke detail dinas
  const handleDetailDinas = (dinasId, namaDinas) => {
    navigate(
      `/daily-performance/laporan-harian/perdinas-detail/${dinasId}?tanggalAwal=${tanggalAwal}&tanggalAkhir=${tanggalAkhir}&namaDinas=${namaDinas}`
    );
  };

  // Fungsi untuk export Excel
  const handleExportExcel = () => {
    if (!filteredData?.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat HTML table untuk Excel
    const tableHeaders = [
      "No",
      "Nama Dinas",
      "Total Pegawai",
      "Total Laporan",
      "Total Kegiatan",
      "Total Output",
      "Rata-rata Kegiatan/Laporan",
      "Rata-rata Output/Kegiatan",
      "Persentase Kepatuhan",
      "Rata-rata Kegiatan/Hari",
    ];

    let tableRows = "";
    filteredData.forEach((dinas, index) => {
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${dinas.nama_dinas || ""}</td>
          <td>${dinas.total_pegawai || 0}</td>
          <td>${dinas.total_laporan || 0}</td>
          <td>${dinas.total_kegiatan || 0}</td>
          <td>${dinas.total_output || 0}</td>
          <td>${dinas.rata_rata_kegiatan_per_laporan || 0}</td>
          <td>${dinas.rata_rata_output_per_kegiatan || 0}</td>
          <td>${dinas.persentase_kepatuhan || 0}%</td>
          <td>${dinas.rata_rata_kegiatan_per_hari || 0}</td>
        </tr>
      `;
    });

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Rekapan Laporan Harian Semua Dinas - ${tanggalAwal} s/d ${tanggalAkhir}</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Rekapan Laporan Harian Semua Dinas</h2>
          <h3>Periode: ${new Date(tanggalAwal).toLocaleDateString(
            "id-ID"
          )} s/d ${new Date(tanggalAkhir).toLocaleDateString("id-ID")}</h3>
          <h3>Filter Posisi: ${
            posisiFilter.diKota ? "Di Kota Kabupaten" : ""
          } ${posisiFilter.diLuarKota ? "Di Luar Kota Kabupaten" : ""}</h3>
          ${
            data
              ? `
          <h3>Statistik Keseluruhan:</h3>
          <ul>
            <li>Total Dinas: ${data.statistik_keseluruhan.total_dinas}</li>
            <li>Total Pegawai: ${data.statistik_keseluruhan.total_pegawai}</li>
            <li>Total Laporan: ${data.statistik_keseluruhan.total_laporan}</li>
            <li>Total Kegiatan: ${data.statistik_keseluruhan.total_kegiatan}</li>
            <li>Total Output: ${data.statistik_keseluruhan.total_output}</li>
            <li>Persentase Kepatuhan: ${data.statistik_keseluruhan.persentase_kepatuhan_keseluruhan}%</li>
            <li>Rata-rata Kegiatan/Hari: ${data.statistik_keseluruhan.rata_rata_kegiatan_per_hari}</li>
            <li>Hari Kerja: ${data.periode.hari_kerja}</li>
          </ul>
          `
              : ""
          }
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(
      blob,
      `rekapan-laporan-harian-semua-dinas-${tanggalAwal}-${tanggalAkhir}.xls`
    );
  };

  // Columns untuk tabel
  const columns = [
    { Header: "No", accessor: "index", width: "5%" },
    {
      Header: "Nama Dinas",
      accessor: "nama_dinas",
      width: "200px",
      Cell: ({ value }) => (
        <MDBox
          sx={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            maxWidth: "200px",
            py: 1,
          }}
        >
          <MDTypography variant="caption" color="text">
            {value || "-"}
          </MDTypography>
        </MDBox>
      ),
    },
    { Header: "Total Pegawai", accessor: "total_pegawai", width: "10%" },
    { Header: "Total Laporan", accessor: "total_laporan", width: "10%" },
    { Header: "Total Kegiatan", accessor: "total_kegiatan", width: "10%" },
    { Header: "Total Output", accessor: "total_output", width: "10%" },
    {
      Header: "Rata-rata/Laporan",
      accessor: "rata_rata_kegiatan_per_laporan",
      width: "10%",
    },
    {
      Header: "Persentase Kepatuhan",
      accessor: "persentase_kepatuhan",
      width: "12%",
      Cell: ({ value }) => (
        <Chip
          label={`${parseFloat(value).toFixed(2)}%`}
          color={
            parseFloat(value) >= 80
              ? "success"
              : parseFloat(value) >= 60
              ? "warning"
              : "error"
          }
          size="small"
          variant="filled"
        />
      ),
    },
    {
      Header: "Detail",
      accessor: "detail",
      width: "8%",
      Cell: ({ row }) => (
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={() =>
            handleDetailDinas(row.original.dinas_id, row.original.nama_dinas)
          }
          sx={{ minWidth: "auto", px: 1 }}
        >
          <Icon sx={{ fontSize: 16 }}>visibility</Icon>
        </MDButton>
      ),
    },
  ];

  // Rows untuk tabel
  const rows =
    filteredData
      ?.sort((a, b) => {
        // Urutkan berdasarkan persentase kepatuhan dari tertinggi ke terendah
        const persentaseA = parseFloat(a.persentase_kepatuhan) || 0;
        const persentaseB = parseFloat(b.persentase_kepatuhan) || 0;
        return persentaseB - persentaseA; // Descending order (tertinggi ke terendah)
      })
      ?.map((dinas, index) => ({
        index: index + 1,
        dinas_id: dinas.dinas_id,
        nama_dinas: dinas.nama_dinas,
        total_pegawai: dinas.total_pegawai,
        total_laporan: dinas.total_laporan,
        total_kegiatan: dinas.total_kegiatan,
        total_output: dinas.total_output,
        rata_rata_kegiatan_per_laporan: dinas.rata_rata_kegiatan_per_laporan,
        persentase_kepatuhan: dinas.persentase_kepatuhan,
      })) || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          {/* Statistik Card dengan Filter Tanggal dan Posisi */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox mb={3}>
                  <MDBox
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    gap={2}
                  >
                    <MDTypography variant="h6" gutterBottom>
                      Rekapan Laporan Harian Semua Dinas
                    </MDTypography>
                    <MDBox
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                      gap={2}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      width={{ xs: "100%", md: "auto" }}
                    >
                      {/* Filter Posisi */}
                      <FormGroup row={!isMobile}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={posisiFilter.diKota}
                              onChange={handlePosisiChange}
                              name="diKota"
                            />
                          }
                          label="Di Kota"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={posisiFilter.diLuarKota}
                              onChange={handlePosisiChange}
                              name="diLuarKota"
                            />
                          }
                          label="Di Luar Kota"
                        />
                      </FormGroup>
                      <MDInput
                        type="date"
                        label="Tanggal Awal"
                        value={tanggalAwal}
                        onChange={(e) => setTanggalAwal(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth={isMobile}
                      />
                      <MDInput
                        type="date"
                        label="Tanggal Akhir"
                        value={tanggalAkhir}
                        onChange={(e) => setTanggalAkhir(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth={isMobile}
                      />
                    </MDBox>
                  </MDBox>
                </MDBox>

                {data && (
                  <>
                    <MDTypography variant="button" color="text" gutterBottom>
                      Periode:{" "}
                      {new Date(data.periode.tanggal_awal).toLocaleDateString(
                        "id-ID"
                      )}{" "}
                      s/d{" "}
                      {new Date(data.periode.tanggal_akhir).toLocaleDateString(
                        "id-ID"
                      )}{" "}
                      ({data.periode.hari_kerja} hari kerja)
                    </MDTypography>

                    <Grid container spacing={isMobile ? 2 : 3} mt={2}>
                      <Grid item xs={6} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography
                            variant={isMobile ? "h5" : "h4"}
                            color="info"
                          >
                            {filteredStats?.total_dinas ??
                              data.statistik_keseluruhan.total_dinas}
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            color="text"
                            fontSize={isMobile ? "0.75rem" : "0.875rem"}
                          >
                            Total Dinas
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography
                            variant={isMobile ? "h5" : "h4"}
                            color="success"
                          >
                            {filteredStats?.total_pegawai ??
                              data.statistik_keseluruhan.total_pegawai}
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            color="text"
                            fontSize={isMobile ? "0.75rem" : "0.875rem"}
                          >
                            Total Pegawai
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography
                            variant={isMobile ? "h5" : "h4"}
                            color="primary"
                          >
                            {filteredStats?.total_laporan ??
                              data.statistik_keseluruhan.total_laporan}
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            color="text"
                            fontSize={isMobile ? "0.75rem" : "0.875rem"}
                          >
                            Total Laporan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography
                            variant={isMobile ? "h5" : "h4"}
                            color="warning"
                          >
                            {filteredStats?.total_kegiatan ??
                              data.statistik_keseluruhan.total_kegiatan}
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            color="text"
                            fontSize={isMobile ? "0.75rem" : "0.875rem"}
                          >
                            Total Kegiatan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography
                            variant={isMobile ? "h5" : "h4"}
                            color="secondary"
                          >
                            {filteredStats?.total_output ??
                              data.statistik_keseluruhan.total_output}
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            color="text"
                            fontSize={isMobile ? "0.75rem" : "0.875rem"}
                          >
                            Total Output
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography
                            variant={isMobile ? "h5" : "h4"}
                            color="dark"
                          >
                            {filteredStats?.rata_rata_kegiatan_per_hari ??
                              data.statistik_keseluruhan
                                .rata_rata_kegiatan_per_hari}
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            color="text"
                            fontSize={isMobile ? "0.75rem" : "0.875rem"}
                          >
                            Rata-rata/Hari
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </Grid>

                    {/* Progress Bar */}
                    <MDBox mt={3}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <MDTypography variant="button" color="text">
                          Persentase Kepatuhan Keseluruhan
                        </MDTypography>
                        <MDTypography
                          variant="button"
                          color={
                            data.statistik_keseluruhan
                              .persentase_kepatuhan_keseluruhan >= 80
                              ? "success"
                              : data.statistik_keseluruhan
                                  .persentase_kepatuhan_keseluruhan >= 60
                              ? "warning"
                              : "error"
                          }
                        >
                          {
                            data.statistik_keseluruhan
                              .persentase_kepatuhan_keseluruhan
                          }
                          %
                        </MDTypography>
                      </MDBox>
                      <MDProgress
                        variant="determinate"
                        value={
                          filteredStats?.persentase_kepatuhan_keseluruhan ??
                          data.statistik_keseluruhan
                            .persentase_kepatuhan_keseluruhan
                        }
                        color={
                          (filteredStats?.persentase_kepatuhan_keseluruhan ??
                            data.statistik_keseluruhan
                              .persentase_kepatuhan_keseluruhan) >= 80
                            ? "success"
                            : (filteredStats?.persentase_kepatuhan_keseluruhan ??
                                data.statistik_keseluruhan
                                  .persentase_kepatuhan_keseluruhan) >= 60
                            ? "warning"
                            : "error"
                        }
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Tabel Data Dinas */}
          {data && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDBox>
                      <MDTypography variant="h6" color="text">
                        Data Dinas:{" "}
                        {new Date(tanggalAwal).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}{" "}
                        s/d{" "}
                        {new Date(tanggalAkhir).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total: {filteredData.length} dinas
                      </MDTypography>
                    </MDBox>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={handleExportExcel}
                      disabled={loading}
                    >
                      <Icon>download</Icon>
                      &nbsp;Export Excel
                    </MDButton>
                  </MDBox>
                  {isMobile ? (
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {loading && (
                        <MDTypography variant="button" color="text.secondary">
                          Memuat...
                        </MDTypography>
                      )}
                      {!loading &&
                        rows.map((row, index) => (
                          <Card
                            key={`${
                              row.dinas_id || row.nama_dinas || "row"
                            }-${index}`}
                          >
                            <CardContent>
                              <MDTypography variant="button" fontWeight="bold">
                                {index + 1}. {row.nama_dinas}
                              </MDTypography>
                              <MDBox
                                mt={1}
                                display="flex"
                                flexWrap="wrap"
                                gap={0.5}
                              >
                                <Chip
                                  label={`Pegawai: ${row.total_pegawai}`}
                                  color="info"
                                  size="small"
                                />
                                <Chip
                                  label={`Laporan: ${row.total_laporan}`}
                                  color="primary"
                                  size="small"
                                />
                                <Chip
                                  label={`Kegiatan: ${row.total_kegiatan}`}
                                  color="warning"
                                  size="small"
                                />
                                <Chip
                                  label={`Output: ${row.total_output}`}
                                  color="secondary"
                                  size="small"
                                />
                                <Chip
                                  label={`Rata2/Laporan: ${row.rata_rata_kegiatan_per_laporan}`}
                                  size="small"
                                />
                              </MDBox>
                              <MDBox mt={1.25}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Persentase Kepatuhan
                                </MDTypography>
                                <MDBox mt={0.5}>
                                  <MDProgress
                                    variant="gradient"
                                    value={
                                      parseFloat(row.persentase_kepatuhan) || 0
                                    }
                                    color={
                                      (parseFloat(row.persentase_kepatuhan) ||
                                        0) >= 80
                                        ? "success"
                                        : (parseFloat(
                                            row.persentase_kepatuhan
                                          ) || 0) >= 60
                                        ? "warning"
                                        : "error"
                                    }
                                    sx={{
                                      width: "100%",
                                      height: 8,
                                      borderRadius: 8,
                                    }}
                                  />
                                </MDBox>
                                <MDBox
                                  mt={0.5}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {(
                                      parseFloat(row.persentase_kepatuhan) || 0
                                    ).toFixed(2)}
                                    %
                                  </MDTypography>
                                  <MDButton
                                    variant="gradient"
                                    color="info"
                                    size="small"
                                    onClick={() =>
                                      handleDetailDinas(
                                        row.dinas_id,
                                        row.nama_dinas
                                      )
                                    }
                                    sx={{ minWidth: "auto", px: 1 }}
                                  >
                                    <Icon sx={{ fontSize: 16 }}>
                                      visibility
                                    </Icon>
                                    &nbsp;Detail
                                  </MDButton>
                                </MDBox>
                              </MDBox>
                            </CardContent>
                          </Card>
                        ))}
                    </MDBox>
                  ) : (
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={pageSize}
                      showTotalEntries={true}
                      noEndBorder
                      loading={loading}
                      canSearch={false}
                      pagination={{
                        page: currentPage,
                        pageSize: pageSize,
                        onPageChange: (page) => setCurrentPage(page),
                        onPageSizeChange: (size) => {
                          setPageSize(size);
                          setCurrentPage(0);
                        },
                      }}
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Loading State */}
          {loading && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDTypography variant="body1" color="text">
                    Memuat data rekapan dinas...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Empty State */}
          {!data && !loading && tanggalAwal && tanggalAkhir && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={4}
                  >
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
                      event_busy
                    </Icon>
                    <MDTypography variant="h6" color="text.secondary">
                      Tidak ada data rekapan dinas
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Coba pilih periode tanggal lain atau periksa kembali
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default RekapanLaporanDinas;
